import React from 'react';
import * as XLSX from 'xlsx';
import { Download } from 'lucide-react';
import { Button } from '@mui/material';

/**
 * ExportExcel Component
 * Reusable button component to export JSON data to Excel file
 *
 * Props:
 *   - data: Array of objects to export (required)
 *   - fileName: Name of the Excel file (default: 'export.xlsx')
 *   - sheetName: Name of the sheet (default: 'Sheet1')
 *   - buttonLabel: Text to display on button (default: 'Export to Excel')
 *   - variant: Button variant - 'contained' | 'outlined' | 'text' (default: 'contained')
 *   - color: Button color - 'primary' | 'secondary' | 'success' | 'error' | 'warning' | 'info' (default: 'primary')
 *   - icon: Custom icon to display (default: Download icon)
 *   - onError: Error callback function (optional)
 *   - exportKeys: Array of keys to export from the data objects (optional). If not provided, exports all keys.
 *   - exportHeaders: Array of custom header names corresponding to exportKeys (optional).
 *
 * Usage:
 *   <ExportExcel
 *     data={jsonData}
 *     fileName="myfile.xlsx"
 *     buttonLabel="Download"
 *     exportKeys={['id', 'name']}
 *     exportHeaders={['ID', 'Full Name']}
 *   />
 */

function ExportExcel({
  data = [],
  fileName = 'export.xlsx',
  sheetName = 'Sheet1',
  buttonLabel = 'Export to Excel',
  icon = null,
  onError = null,
  variant = 'contained',
  color = 'primary',
  exportKeys = null,
  exportHeaders = null
}) {
  const handleExport = () => {
    try {
      // Validate data
      if (!Array.isArray(data) || data.length === 0) {
        const errorMsg = 'No data to export';
        console.warn(errorMsg);
        if (onError) onError(errorMsg);
        return;
      }

      // Filter and map data if exportKeys are provided
      let finalData = data;
      if (exportKeys && Array.isArray(exportKeys) && exportKeys.length > 0) {
        finalData = data.map((item) => {
          const newItem = {};
          exportKeys.forEach((key, index) => {
            const headerName =
              exportHeaders && Array.isArray(exportHeaders) && exportHeaders[index]
                ? exportHeaders[index]
                : key;
            newItem[headerName] = item[key];
          });
          return newItem;
        });
      }

      // Create workbook and worksheet
      const ws = XLSX.utils.json_to_sheet(finalData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, sheetName);

      // Set column widths for better readability
      const colWidths = Object.keys(finalData[0]).map((key) => ({
        wch: Math.max(key.length, 15)
      }));
      ws['!cols'] = colWidths;

      // Generate file name with timestamp if needed
      const finalFileName = fileName.endsWith('.xlsx') ? fileName : `${fileName}.xlsx`;

      // Write file
      XLSX.writeFile(wb, finalFileName);
    } catch (error) {
      console.log('Export error:', error);
      if (onError) onError(error.message);
    }
  };

  return (
    <Button
      variant={variant}
      color={color}
      size="small"
      startIcon={icon || <Download size={15} />}
      onClick={handleExport}
      title={`Export to Excel - ${fileName}`}
      sx={{
        textTransform: 'none',
        fontWeight: 600,
        borderRadius: 1.5,
        px: 2,
        py: 0.8,
        fontSize: '0.85rem'
      }}
    >
      {buttonLabel}
    </Button>
  );
}

export default ExportExcel;
